import React, { useContext, useEffect, useState } from "react";
import { OverlayTrigger, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiPencil } from "react-icons/bi";
import { BsXSquare } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../App";
import PaginationComponent from "../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE, LS_AUTHORITY_KEY, ReportedFrequency } from "../../../core/constants/common";
import KPIService from "../../../core/service/KPIService";
import Utils from "../../../core/utilities/Utils";
import "./KPI.scss";
import StandardService from "../../../core/service/StandardService";

const KPIDataView = ({ t, filter }) => {

	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsData.profile.company.id;
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [goals, setGoals] = useState({});
	const [goalVisibility, setGoalVisibility] = useState({});
	const [kpiVisibility, setKPIVisibility] = useState({});
	const [itemsTotalCount, setItemTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
	const [currentPage, setCurrentPage] = useState(1);
	const yearToFilter = filter.reportingPeriod ? filter.reportingPeriod.split('-')[0] : new Date().getFullYear();
	const [monthsOfYear, setMonthsOfyear] = useState(Utils.getMonthsForYear(yearToFilter));
	const [editedPeriod, setEditedPeriod] = useState(0);
	const [dataToSave, setDataToSave] = useState([]);
	const [uomList, setUOMList] = useState([]);

	useEffect(() => {
		fetchData(0, itemsPerPage, filter);
		getUOMs();
	}, [filter]);

	const fetchData = (pageNumber, itemsPerPageVal, filter) => {
		setMonthsOfyear(Utils.getMonthsForYear(filter.reportingPeriod.split('-')[0]));
		let pageNum, perPage;
		if (pageNumber > 0) {
			pageNum = pageNumber - 1;
		} else {
			setCurrentPage(1);
		}
		if (itemsPerPageVal > 0) {
			perPage = itemsPerPageVal;
		} else {
			perPage = itemsPerPage;
		}
		setLoaderDisplay(true);
		KPIService.listKPIDataPaged({
			companyId: companyId,
			pageNumber: pageNum,
			pageSize: perPage,
			period: filter.reportingPeriod,
			category: filter.kpiCategory,
			goalId: filter.goalId,
			kpiId: filter.kpiId,
			assigneeId: filter.entityId
		}).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			if (res.data.data) {
				setItemTotalCount(res.data.data.totalElements);
				setGoals(res.data.data.content ?? []);
			} else {
				setItemTotalCount(0);
				setGoals([]);
			}
		}).catch(err => { setLoaderDisplay(false); });
	};

	const getUOMs = () => {
		StandardService.getUnitsOfMeasure()
			.then(res => {
				if (res.status !== 200) return;

				setUOMList(res.data.data);
			})
			.catch((error) => { });
	};

	const renderTableHeader = () => {
		return (
			<thead>
				<tr>
					<th>{t("GoalsandKPIs.label")}</th>
					<th></th>
					<th className="text-center">{t("Unit.label")}</th>
					<th className="text-center">{t("Total.label")}</th>
					{
						monthsOfYear?.map((month, monthIndex) => {
							return (
								<th key={`monthHeader-${monthIndex}`} className="nowrap text-center">
									<span>{month.value}</span>&nbsp;
									{renderColumnActionIcon(month.index)}
								</th>
							);
						})
					}
				</tr>
			</thead>
		);
	};

	const renderColumnActionIcon = (index) => {
		return (
			<span>
				{
					editedPeriod === index
						?
						<OverlayTrigger
							key={'cancelOverlay' + index}
							placement="top"
							overlay={Utils.renderTooltip(t("CancelEdit.label"))}>
							<span>
								<BsXSquare
									className="cursor"
									onClick={() => setEditedPeriod(0)}>
								</BsXSquare>{" "}
							</span>
						</OverlayTrigger>
						:
						<OverlayTrigger
							key={'editOverlay' + index}
							placement="top"
							overlay={Utils.renderTooltip(t("Edit.label"))}>
							<span>
								<BiPencil
									className="cursor"
									onClick={() => onPeriodEdit(index)} />
							</span>
						</OverlayTrigger>
				}
			</span>
		);
	};

	const onPeriodEdit = (index) => {
		setEditedPeriod(index);
	};

	const showOrHideGoal = (goalId) => {
		const vis = { ...goalVisibility };
		vis[goalId] = vis[goalId] === 'hide' ? 'show' : 'hide';
		setGoalVisibility(vis);
	};

	const showOrHideKPI = (kpiId) => {
		const vis = { ...kpiVisibility };
		vis[kpiId] = vis[kpiId] === 'hide' ? 'show' : 'hide';
		setKPIVisibility(vis);
	};

	const onPeriodCellValChange = (val, kpiId, dp, month) => {
		const dToSave = JSON.parse(JSON.stringify(dataToSave));
		const matchingIndex = dToSave?.findIndex(d => d.kpiId === kpiId && d.dataPointId === dp.id && d.period === month.date);
		if (matchingIndex >= 0) {
			dToSave[matchingIndex].value = +val;
		} else {
			const obj = {
				ownerId: companyId,
				kpiId: kpiId,
				dataPointId: dp.id,
				uom: dp.unitOfMeasure,
				value: +val,
				period: month.date
			};
			if (filter.entityId > 0) {
				obj.assigneeId = filter.entityId;
			}
			dToSave.push(obj);
		}
		setDataToSave(dToSave);
	};

	const onPeriodCellUOMChange = (val, kpiId, dp, month) => {
		const dToSave = JSON.parse(JSON.stringify(dataToSave));
		const matchingIndex = dToSave?.findIndex(d => d.kpiId === kpiId && d.dataPointId === dp.id && d.period === month.date);
		if (matchingIndex >= 0) {
			dToSave[matchingIndex].uom = val;
		} else {
			dToSave.push({
				ownerId: companyId,
				kpiId: kpiId,
				dataPointId: dp.id,
				uom: val,
				value: dp.value,
				period: month.date
			});
		}
		setDataToSave(dToSave);
	};

	const renderDPPeriodCell = (kpi, dp, dpIndex, month) => {
		if (!isKPICellActive(kpi, month.index)) {
			return <td key={`dpCell-${dp.id}-${dpIndex}-${month.index}`} className="disabled-cell"> <span className="KPibadge01"></span></td>;
		}
		return (
			<td key={`dpCell-${dp.id}-${dpIndex}-${month.index}`} className="text-center 1">
				{
					editedPeriod === month.index
						?
						<div className='w-100 d-flex align-items-center'>
							<div className='w-60 AddUnit_kpi'>
								<input
									type="text"
									className="form-control branch-col"
									id={`dpCellValue-${dp.id}-${dpIndex}-${month.index}`}
									defaultValue={dp?.data[month.date]?.value}
									name="value"
									onChange={(e) => onPeriodCellValChange(e.target.value, kpi.id, dp, month)}
								/>
							</div>
							<div className="w-40 ms-2 Unit_View_kpi">
								<select className='form-select'
									id={`dpUOMField-${dp.id}-${dpIndex}-${month.index}`}
									required={true}
									value={dp.unitOfMeasure}
									onChange={(e) => onPeriodCellUOMChange(e.target.value, kpi.id, dp, month)}>
									{
										<>
											<option key='' value=''>{t("Select.label")}</option>
											{
												uomList?.map(u => {
													return <option key={u.id} value={u.inputUnit}>{u.inputUnit}</option>;
												})
											}
										</>
									}
								</select>
							</div>
						</div>
						:
						<span>{dp?.data[month.date]?.value ?? ''} </span>
				}
			</td>
		);
	};

	const onKPIPeriodCellValChange = (val, type, kpi, month) => {
		const dToSave = JSON.parse(JSON.stringify(dataToSave));
		const matchingData = dToSave?.filter(d => d.kpiId === kpi.id && !d.dataPointId && d.period === month.date);
		if (matchingData?.length > 0) {
			matchingData.forEach(mData => {
				mData[type] = +val;
			});
		} else {
			const obj = {
				ownerId: companyId,
				kpiId: kpi.id,
				uom: kpi.unit,
				period: month.date,
			};
			obj[type] = +val;
			if (filter.entityId > 0) {
				obj.assigneeId = filter.entityId;
			}
			dToSave.push(obj);
		}
		setDataToSave(dToSave);
	};

	const getKPIPeriodValueToConsider = (type, kpi, month) => {
		if (kpi?.periodicData[month.date] && kpi?.periodicData[month.date][type]) {
			return kpi.periodicData[month.date][type];
		} else {
			return kpi[type];
		}
	};

	const renderKPIPeriodCell = (type, kpi, month) => {
		if (!isKPICellActive(kpi, month.index)) {
			return <td key={`kpiPeriodCell-${type}-${kpi.id}-${month.index}`} className="disabled-cell"> <span className="KPibadge01"></span></td>;
		}
		return (
			<td key={`kpiPeriodCell-${type}-${kpi.id}-${month.index}`} className="text-center 2">
				{
					editedPeriod === month.index
						?
						<div className='w-100 d-flex align-items-center'>
							<div className='w-100'>
								<input
									type="text"
									className="form-control branch-col"
									id={`kpiPeriodCellValue-${type}-${kpi.id}-${month.index}`}
									defaultValue={getKPIPeriodValueToConsider(type, kpi, month)}
									name="value"
									onChange={(e) => onKPIPeriodCellValChange(e.target.value, type, kpi, month)}
								/>
							</div>
							{/* <span>&nbsp;{kpi.unit}</span> */}
						</div>
						:
						<span>{getKPIPeriodValueToConsider(type, kpi, month)} </span>
				}
			</td>
		);
	};

	const renderKPIDataPointRow = (goalId, kpi, dp, dpIndex) => {
		const isHidden = kpiVisibility[kpi.id] === 'hide'
			|| goalVisibility[goalId] === 'hide';
		return (
			<>
				<tr key={`dpRow-${kpi.id}-${dpIndex}`}
					className={isHidden ? 'kpi-dp-row d-none' : 'kpi-dp-row'}>
					<td className="name-cell ml-2">{dp.name}</td>
					<td></td>
					<td>{dp.unitOfMeasure}</td>
					<td></td>
					{
						monthsOfYear?.map((month, monthIndex) => {
							return renderDPPeriodCell(kpi, dp, dpIndex, month)
						})
					}
				</tr>
			</>
		);
	};

	const isKPICellActive = (kpi, monthIndex) => {
		return kpi.measured === ReportedFrequency.MONTHLY
			|| (kpi.measured === ReportedFrequency.QUARTERLY && (monthIndex % 3 === 0))
			|| (kpi.measured === ReportedFrequency.HALF_YEARLY && (monthIndex % 6 === 0))
			|| (kpi.measured === ReportedFrequency.YEARLY && (monthIndex % 12 === 0));
	};

	const renderGoalKPIRow = (goalId, kpi, kpiIndex) => {
		const isHidden = goalVisibility[goalId] === 'hide';
		return (
			<>
				<tr key={`kpiRow-${goalId}-${kpiIndex}`}
					className={isHidden ? 'kpi-row d-none' : 'kpi-row'}>
					<td className="name-cell ml-2">
						<div className="d-flex">
							<span className="Kpileft_arrow" onClick={() => showOrHideKPI(kpi.id)}>
								{kpiVisibility[kpi.id] === 'hide' ? <FaChevronDown /> : <FaChevronUp />}
							</span>
							<span className="fw-600">{kpi.name}</span>
						</div>
					</td>
					<td className="white-space">Actual</td>
					<td className="white-space">{kpi.unit}</td>
					<td className="text-center fw-bold">{kpi.solution === null ? '' : `${kpi.solution}`}</td>
					{
						monthsOfYear?.map((month, monthIndex) => {
							return <>
								{
									isKPICellActive(kpi, month.index)
										?
										<td className="text-center 3">
											<span className={`KPibadge01 ${getClassNameForActualValue(kpi, month)}`}>
												{kpi?.periodicData[month.date]?.solution !== null && kpi?.periodicData[month.date]?.solution !== undefined ? `${kpi?.periodicData[month.date]?.solution} ` : ''}
											</span>
										</td>
										:
										<td className="disabled-cell">
											<span className="KPibadge01"></span>
										</td>
								}
							</>;
						})
					}
				</tr>
				<tr key={`dpTargetRow-${kpi.id}`}
					className={isHidden ? 'kpi-dp-row d-none' : 'kpi-dp-row'}>
					<td className="name-cell ml-2"></td>
					<td className="white-space">Target</td>
					<td className="white-space">{kpi.unit}</td>
					<td className="text-center 4 fw-bold">{kpi.target} </td>
					{
						monthsOfYear?.map((month, monthIndex) => {
							return renderKPIPeriodCell('target', kpi, month)
						})
					}
				</tr>
				<tr key={`dpRedFlagRow-${kpi.id}}`}
					className={isHidden ? 'kpi-dp-row d-none' : 'kpi-dp-row'}>
					<td className="name-cell ml-2"></td>
					<td className="white-space">Red Flag</td>
					<td className="white-space">{kpi.unit}</td>
					<td className="text-center 5 fw-bold">{kpi.redFlag} </td>
					{
						monthsOfYear?.map((month, monthIndex) => {
							return renderKPIPeriodCell('redFlag', kpi, month)
						})
					}
				</tr>
				{
					kpi?.dataPoints?.map((dp, dpIndex) => {
						return renderKPIDataPointRow(goalId, kpi, dp, dpIndex);
					})
				}
			</>
		);
	};

	const renderGoalRow = (goal, goalIndex) => {
		return (
			<>
				<tr key={`goalRow-${goalIndex}`}
					className='goal-row'>
					<td className="name-cell cursor" colSpan="16" onClick={() => showOrHideGoal(goal.id)}>
						<span className="DataTopic_sticky">
							<span className="Kpileft_arrow_head" >
								{goalVisibility[goal.id] === 'hide' ? <FaChevronDown /> : <FaChevronUp />}
							</span>
							<span className="fw-bold">{goal.name}</span>
						</span>
					</td>
					{/* <td></td>
					<td></td>
					<td colSpan="12"></td> */}
				</tr>
				{
					goal.kpis?.map((kpi, kpiIndex) => {
						return renderGoalKPIRow(goal.id, kpi, kpiIndex);
					})
				}
			</>
		);
	};

	const renderEmptyDataLabel = () => {
		return (
			<tr>
				<td colSpan="15" className="text-center 6">
					<div className="Nodatafound_box">
						<h6><FcFinePrint className="me-2" />{t("Nodatafound.label")}</h6>
					</div>
				</td>
			</tr>
		)
	};

	const saveData = () => {
		if (dataToSave.length === 0) return;

		setLoaderDisplay(true);
		KPIService.upsertKPIData(dataToSave).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 201 && res.status !== 200) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			setEditedPeriod(0);
			setDataToSave([]);
			fetchData(currentPage, itemsPerPage, filter);
		}).catch(err => {
			setLoaderDisplay(false);
		});
	}

	const getClassNameForActualValue = (kpi, month) => {
		if (kpi?.periodicData[month.date]?.solution === null) {
			return '';
		}
		const solution = kpi?.periodicData[month.date]?.solution ?? 0;
		let target;
		if (kpi?.periodicData[month.date]?.target !== null) {
			target = kpi?.periodicData[month.date]?.target;
		} else {
			target = kpi.target;
		}
		let redFlag;
		if (kpi?.periodicData[month.date]?.redFlag !== null) {
			redFlag = kpi?.periodicData[month.date]?.redFlag;
		} else {
			redFlag = kpi.redFlag;
		}

		if (redFlag > target) {
			if (solution > redFlag) {
				return 'bg-danger1';
			} else if (solution > target && solution <= redFlag) {
				return 'bg-warning1';
			} else if (solution <= target) {
				return 'bg-success1';
			}
		} else {
			if (solution < redFlag) {
				return 'bg-danger1';
			} else if (solution < target && solution >= redFlag) {
				return 'bg-warning1';
			} else if (solution >= target) {
				return 'bg-success1';
			}
		}
	};

	return (
		<div id='kpiDataViewHolder'>
			<div className="table-holder table-responsive mt-2">
				<Table responsive="sm" bordered >
					{renderTableHeader()}
					<tbody>
						{
							goals.length > 0
								?
								goals?.map((goal, goalIndex) => {
									return renderGoalRow(goal, goalIndex);
								})
								:
								renderEmptyDataLabel()
						}
					</tbody>
				</Table>
			</div>
			<div className="p-2 text-center">
				<button type="button"
					onClick={() => saveData()}
					className="btn btn-primary ms-2 px-4 sbmBtn">
					{t("Save.label")}
				</button>
			</div>
			<div className="p-2 List_footer_count">
				<PaginationComponent
					itemsTotalCount={itemsTotalCount}
					itemsPerPage={itemsPerPage}
					currentPage={currentPage}
					setCurrentPage={setCurrentPage}
					alwaysShown={true}
					setItemsPerPage={setItemsPerPage}
					pageClicked={(pageNum, perPage) => {
						fetchData(filter.reportingPeriod, pageNum, perPage);
					}} />
			</div>
		</div>
	);
};

export default withTranslation()(KPIDataView);
